import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { OrganizationSwitcher, UserButton, useOrganization } from '@clerk/react';
import { cn } from '@/lib/utils';
import { Logo } from '@/components/Logo';
import { CoreIcon } from '@/components/CoreIcon';
import { Tooltip } from '@/components/Tooltip';

const NAV: Array<
  | { kind: 'section'; label: string }
  | { kind: 'link'; to: string; label: string; icon: string; badge?: 'soon' }
> = [
  { kind: 'section', label: 'Work' },
  { kind: 'link', to: 'inbox',     label: 'Inbox',      icon: 'inbox' },
  { kind: 'link', to: 'customers', label: 'Customers',  icon: 'people' },
  { kind: 'link', to: 'reports',   label: 'Reports',    icon: 'chart-line' },

  { kind: 'section', label: 'Configure' },
  { kind: 'link', to: 'tools',     label: 'Tools',      icon: 'puzzle' },
  { kind: 'link', to: 'team',      label: 'Team',       icon: 'user-plus' },
  { kind: 'link', to: 'settings',  label: 'Settings',   icon: 'settings' },
];

export function AppShell() {
  const { organization } = useOrganization();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="h-screen flex bg-muted/30">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-background focus:px-3 focus:py-1.5 focus:rounded focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      <aside
        className={cn(
          'bg-sidebar text-white flex flex-col border-r border-black/20 transition-[width] duration-200 ease-btn shrink-0',
          collapsed ? 'w-16' : 'w-64',
        )}
        aria-label="Primary sidebar"
      >
        <div className={cn(
          'h-16 border-b border-white/10 flex items-center overflow-hidden',
          collapsed ? 'justify-center px-0' : 'pl-3',
        )}>
          <Link to="/app/inbox" aria-label="edgemail — go to inbox" className="flex items-center">
            {collapsed ? (
              <Logo variant="icon" size={28} invert />
            ) : (
              <Logo variant="full" size={64} invert />
            )}
          </Link>
        </div>

        <nav className={cn('flex-1 overflow-y-auto', collapsed ? 'p-1.5' : 'p-3 space-y-0.5')} aria-label="Main navigation">
          {NAV.map((n, i) =>
            n.kind === 'section' ? (
              collapsed ? (
                <div key={`sec-${i}`} className="my-2 mx-auto w-6 border-t border-white/10" />
              ) : (
                <div
                  key={`sec-${i}`}
                  className="mono-label mt-4 mb-1 px-3 text-white/40"
                >
                  {n.label}
                </div>
              )
            ) : (
              <Tooltip key={n.to} content={n.label} side="right" enabled={collapsed}>
                <NavLink
                  to={n.to}
                  end={n.to === 'inbox' ? false : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center rounded-md text-sm transition-colors',
                      collapsed ? 'justify-center p-2.5 my-0.5' : 'gap-2.5 px-3 py-2',
                      isActive || loc.pathname.startsWith(`/app/${n.to}`)
                        ? 'bg-white/10 text-white'
                        : 'text-white/70 hover:bg-white/5 hover:text-white',
                    )
                  }
                >
                  <CoreIcon name={n.icon} size={18} />
                  {!collapsed && <span className="flex-1">{n.label}</span>}
                  {!collapsed && n.badge === 'soon' && (
                    <span className="text-[10px] uppercase tracking-mono text-white/50">
                      soon
                    </span>
                  )}
                </NavLink>
              </Tooltip>
            ),
          )}
        </nav>

        <div className={cn('border-t border-white/10', collapsed ? 'p-1.5' : 'p-3 space-y-2')}>
          {!collapsed && organization && (
            <div className="text-xs text-white/60 truncate" aria-label="Current organization">
              {organization.name}
            </div>
          )}
          <div className={cn(
            'flex items-center',
            collapsed ? 'flex-col gap-2' : 'justify-between',
          )}>
            {!collapsed && (
              <OrganizationSwitcher
                hidePersonal
                appearance={{ elements: { rootBox: 'text-white' } }}
              />
            )}
            <UserButton />
          </div>
        </div>

        <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} side="right" enabled={collapsed}>
          <button
            onClick={() => setCollapsed(c => !c)}
            className={cn(
              'flex items-center justify-center h-10 border-t border-white/10 text-white/50 hover:text-white hover:bg-white/5 transition-colors',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <CoreIcon name={collapsed ? 'chevron-right' : 'chevron-left'} size={16} />
          </button>
        </Tooltip>
      </aside>
      <main id="main-content" className="flex-1 min-w-0 overflow-hidden" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
